import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Loader2, RotateCw, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { shortDate } from "@/lib/invoice";
import { Button } from "@/components/ui/button";

export default function QboFailures({ connected }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/qbo/failures").then((r) => setRows(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);

  const retry = async (ids, key, force = false) => {
    if (force && !window.confirm("Force-send to QuickBooks? This creates a brand-new invoice there even if one already exists, which can make a duplicate. Continue?")) return;
    setBusy(key);
    try {
      const { data } = await api.post("/qbo/send", { ids, force });
      const bad = data.results.filter((r) => !r.ok);
      data.sent && toast.success(`${data.sent} invoice${data.sent === 1 ? "" : "s"} ${force ? "force-sent" : "sent"} to QuickBooks`);
      bad.length && toast.error(`${bad.length} still failed: ${bad[0].error}`);
    } catch (e) { toast.error(formatError(e)); }
    setBusy(null);
    load();
  };

  const clear = async () => {
    if (!window.confirm(`Clear all ${rows.length} failed sends from this list? The invoices are not changed and can still be sent later.`)) return;
    setBusy("clear");
    try {
      const { data } = await api.post("/qbo/failures/clear");
      toast.success(`Cleared ${data.cleared} failed send${data.cleared === 1 ? "" : "s"}`);
    } catch (e) { toast.error(formatError(e)); }
    setBusy(null);
    load();
  };

  if (!rows) return <Loader2 className="mt-4 animate-spin text-primary" />;
  return (
    <div className="mt-4 border-t pt-4" data-testid="qbo-failures">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium flex items-center gap-2">
          {rows.length ? <AlertTriangle size={15} className="text-red-400" /> : null} Failed sends <span className="text-muted-foreground" data-testid="qbo-failures-count">({rows.length})</span>
        </span>
        {rows.length > 0 && <Button size="sm" variant="outline" className="ml-auto" disabled={!!busy || !connected} onClick={() => retry(rows.map((r) => r.id), "all")} data-testid="qbo-retry-all-button">
          {busy === "all" ? <Loader2 size={14} className="animate-spin" /> : <RotateCw size={14} />} Retry all
        </Button>}
        {rows.length > 0 && <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-red-400" disabled={!!busy} onClick={clear} data-testid="qbo-clear-failures-button">
          {busy === "clear" ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Clear list
        </Button>}
      </div>
      {!rows.length && <p className="text-xs text-muted-foreground mt-1" data-testid="qbo-failures-empty">Every invoice you've sent made it to QuickBooks.</p>}
      <div className="mt-2 divide-y max-h-72 overflow-y-auto">
        {rows.map((r) => (
          <div key={r.id} className="py-2 flex items-center gap-3 text-sm" data-testid={`qbo-failure-${r.number}`}>
            <div className="min-w-0 flex-1">
              <p><Link to={`/invoices/${r.id}`} className="font-mono text-primary hover:underline">{r.number}</Link> · {r.customer} · {shortDate(r.service_date)}</p>
              <p className="text-xs text-red-400 truncate" title={r.error}>{r.error}</p>
            </div>
            <Button size="sm" variant="ghost" disabled={!!busy || !connected} onClick={() => retry([r.id], r.id)} data-testid={`qbo-retry-${r.number}`}>
              {busy === r.id ? <Loader2 size={14} className="animate-spin" /> : <RotateCw size={14} />} Retry
            </Button>
            <Button size="sm" variant="ghost" className="text-amber-500 hover:text-amber-400" disabled={!!busy || !connected} onClick={() => retry([r.id], `force-${r.id}`, true)} title="Force a brand-new send to QuickBooks, ignoring this error (may create a duplicate)" data-testid={`qbo-force-${r.number}`}>
              {busy === `force-${r.id}` ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} Force send
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
